/**
 * Reading a drop that may contain folders. `DataTransfer.files` flattens to nothing useful when a
 * folder is dropped, so a sample pack arrives empty; the entry API walks it instead and keeps each
 * file's path inside the drop, which is the taxonomy the pack already shipped (Dax, 2026-09-20).
 *
 * A plain file drop and the file chooser both still work: they simply carry no path.
 */

/** A dropped file with where it sat in the dropped folder, if it sat in one. */
export interface DroppedFile {
  file: File;
  /** "Foley/Glass/clink.wav" for a folder drop; null for a loose file. */
  path: string | null;
}

/** How deep a dropped folder is walked. Packs nest a few levels; anything past this is a mistake. */
const MAX_DEPTH = 8;

interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?: (cb: (f: File) => void, err: (e: unknown) => void) => void;
  createReader?: () => { readEntries: (cb: (entries: FileSystemEntryLike[]) => void, err: (e: unknown) => void) => void };
}

function entryFile(entry: FileSystemEntryLike): Promise<File | null> {
  return new Promise((resolve) => {
    if (!entry.file) return resolve(null);
    entry.file((f) => resolve(f), () => resolve(null));
  });
}

/** readEntries hands back at most 100 at a time, so it is called until it returns nothing. */
async function readAll(reader: { readEntries: (cb: (e: FileSystemEntryLike[]) => void, err: (e: unknown) => void) => void }): Promise<FileSystemEntryLike[]> {
  const out: FileSystemEntryLike[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntryLike[]>((resolve) => {
      reader.readEntries((entries) => resolve(entries), () => resolve([]));
    });
    if (!batch.length) return out;
    out.push(...batch);
  }
}

async function walk(entry: FileSystemEntryLike, prefix: string, depth: number, out: DroppedFile[]): Promise<void> {
  const here = prefix ? `${prefix}/${entry.name}` : entry.name;
  if (entry.isFile) {
    const file = await entryFile(entry);
    // A loose file keeps no path: there is no folder above it to read a tag from.
    if (file) out.push({ file, path: prefix ? here : null });
    return;
  }
  if (!entry.isDirectory || depth >= MAX_DEPTH || !entry.createReader) return;
  for (const child of await readAll(entry.createReader())) await walk(child, here, depth + 1, out);
}

/**
 * Every file in a drop, folders walked. Falls back to the flat list when the browser offers no entry
 * API, so nothing is ever lost, only the paths.
 */
export async function filesFromDrop(dt: DataTransfer): Promise<DroppedFile[]> {
  const items = [...(dt.items ?? [])];
  const entries = items
    .map((i) => (i.kind === 'file' && 'webkitGetAsEntry' in i ? (i.webkitGetAsEntry() as unknown as FileSystemEntryLike | null) : null))
    .filter((e): e is FileSystemEntryLike => Boolean(e));
  if (!entries.length) return [...dt.files].map((file) => ({ file, path: null }));
  const out: DroppedFile[] = [];
  for (const entry of entries) await walk(entry, '', 0, out);
  return out;
}

/** The chooser's files, with the relative path the browser fills in for a folder selection. */
export function filesFromInput(list: FileList | null): DroppedFile[] {
  return [...(list ?? [])].map((file) => ({
    file,
    path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || null,
  }));
}
